import { Close, Done } from '@mui/icons-material'
import {
  Box,
  Divider,
  MobileStepper,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Typography
} from '@mui/material'
import React, { FunctionComponent } from 'react'
import UnitStep1 from './unit/step1'
import UnitStep2 from './unit/step2'
import MNP75Step1 from './mnp75/step1'
import MNP75Step2 from './mnp75/step2'
import { CreateInvoiceReq } from '@/services/mobi-api/invoice/types'
import { UnitOption } from '@/services/mobi-api/unit/types'
import { useMobiApi } from '@/services/mobi-api'
import { useRouter, useSearchParams } from 'next/navigation'
import { useToast } from '@/context/ToastContext'
import { MNP75Unit } from '@/services/mobi-api/mnp75/types'
import useIntl from '@/utils/intl'
import DarkIconButton from '@/components/ui/Buttons/SecondaryIconButton'
import DarkButton from '@/components/ui/Buttons/SecondaryButton'
import Image from 'next/image'
import classNames from 'classnames'
import { resolveImageUrl } from '@/utils/constants'

interface Props {
  onClose: () => void
}

const UnitServices: FunctionComponent<Props> = ({ onClose }) => {
  const router = useRouter()
  const { showError } = useToast()
  const { translate } = useIntl()
  const [currentStep, setCurrentStep] = React.useState(0)
  const [invoiceUnit, setInvoiceUnit] = React.useState<
  CreateInvoiceReq | undefined
  >()

  const [invoiceMNP75, setInvoiceMNP75] = React.useState<
  CreateInvoiceReq | undefined
  >()
  const [product, setProduct] = React.useState<UnitOption | undefined>()
  const [option, setOption] = React.useState<MNP75Unit | undefined>()
  // const [clickedSelection, setClickedSelection] = React.useState<
  // string | undefined
  // >('telephone')
  const searchParams = useSearchParams()
  const defaultSelection = searchParams.get('mnp') === 'true' ? 'MNP75' : 'telephone'
  const [clickedSelection, setClickedSelection] = React.useState<string | undefined>(defaultSelection)
  const { invoice: invoiceApi } = useMobiApi()
  const steps = [
    {
      label: translate('phoneNumber.insert'),
      description: translate('chooseCard')
    },
    {
      label: translate('shop.vat'),
      description: translate('vat.options')
    },
    {
      label: translate('payment'),
      description: translate('apporcard')
    }
  ]
  const stepIcon = (index: number): any => {
    return index === currentStep
      ? (
        <div className='flex items-center justify-center w-5 h-5 p-1 rounded-lg bg-secondary md:h-6 md:w-6'>
          <span className='text-xs font-extrabold text-textInverse md:text-sm'>
            {index + 1}
          </span>
        </div>
        )
      : index <= (currentStep ?? 0)
        ? (
          <div className='bg-[#F0FDF4] border border-[#16A34A] p-2 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <Done className='text-[#16A34A] text-xs md:text-sm font-bold' />
          </div>
          )
        : (
          <div className='bg-[#231F1F1A] p-1 rounded-lg md:h-6 md:w-6 w-5 h-5 flex justify-center items-center'>
            <span className='text-[#B0B0B0] font-extrabold text-xs md:text-sm'>
              {index + 1}
            </span>
          </div>
          )
  }
  const handleNextUnit = (
    payload: CreateInvoiceReq,
    option?: UnitOption
  ): void => {
    const mergedInvoice = {
      ...invoiceUnit,
      ...payload
    }
    setInvoiceUnit(mergedInvoice)

    if (option !== undefined) {
      setProduct(option)
    }
    if (currentStep === 0) {
      setCurrentStep(currentStep + 1)
    } else {
      void createInvoiceUnit(mergedInvoice)
    }
  }
  const handleNextMNP75 = (
    payload: CreateInvoiceReq,
    option?: MNP75Unit
  ): void => {
    const mergedInvoice = {
      ...invoiceMNP75,
      ...payload
    }
    setInvoiceMNP75(mergedInvoice)
    if (option !== undefined) {
      setOption(option)
    }
    if (currentStep === 0) {
      setCurrentStep(currentStep + 1)
    } else {
      void createInvoiceMNP75(mergedInvoice)
    }
  }
  const handleClickStep = (idx: number): void => {
    if (idx < currentStep) {
      setCurrentStep(idx)
    }
  }
  const createInvoiceUnit = async (
    payload: CreateInvoiceReq
  ): Promise<void> => {
    try {
      const res = await invoiceApi.create(payload)
      if (res?.redirectUrl !== undefined && res.redirectUrl !== '') {
        router.push(res.redirectUrl)
      } else {
        showError(translate('error'))
      }
    } catch (e) {
      console.error('CREATE INVOICE FAILED: ', e)
    }
  }
  const createInvoiceMNP75 = async (
    payload: CreateInvoiceReq
  ): Promise<void> => {
    try {
      const res = await invoiceApi.create(payload)
      if (res?.redirectUrl !== undefined && res.redirectUrl !== '') {
        router.push(res.redirectUrl)
      } else {
        showError(translate('error'))
      }
    } catch (e) {
      console.error('CREATE INVOICE FAILED: ', e)
    }
  }
  const chooseService = (service: string): void => {
    setClickedSelection(service)
    setCurrentStep(0)
  }
  return (
    <div className='flex md:flex-row flex-col h-[90vh]'>
      <div className='flex flex-col bg-surfaceInverse xl:w-1/4 lg:w-2/5'>
        <div className='p-4 lg:p-14 md:p-8'>
          <div className='flex-col items-start hidden md:flex'>
            <Image
              draggable={false}
              width={50}
              height={50}
              src={resolveImageUrl('/uploads/mobicom-logo-hq.png')}
              alt='logo'
              className='w-16'
            />
            <div>
              <h1 className='font-bold lg:text-2xl md:text-xl text-textHigh'>
                {translate('quick.buyUnit')}
              </h1>
              <span className='text-xs font-medium text-textMid 2xl:text-sm'>
                {translate('simple')}
              </span>
            </div>
          </div>
          <div className='flex items-center justify-between md:hidden'>
            <div className='flex items-center'>
              <h1 className='pr-4 mr-4 text-sm font-bold border-r text-textHigh'>
                {translate('quick.buyUnit')}
              </h1>
              <MobileStepper
                className='bg-surfaceInverse'
                color='error'
                variant='dots'
                sx={{
                  '.MuiMobileStepper-dotActive': {
                    backgroundColor: 'red'
                  }
                }}
                steps={steps.length}
                position='static'
                activeStep={currentStep}
                backButton={<></>}
                nextButton={<></>}
              />
              <div
                className={classNames(
                  '',
                  currentStep === 0 ? 'invisible' : 'visible'
                )}
              >
                <DarkButton
                  type='soft'
                  fullwidth
                  size='sm'
                  onClick={() => setCurrentStep(currentStep - 1)}
                >
                  {' '}
                  {translate('back')}
                </DarkButton>
              </div>
            </div>
            <DarkIconButton
              type='soft'
              size='sm'
              icon={<Close />}
              onClick={onClose}
            />
          </div>
        </div>
        <Divider className='hidden md:flex' />
        <div className='lg:p-14 md:p-8'>
          <div className='hidden md:flex'>
            <Stepper orientation='vertical' activeStep={currentStep}>
              {steps.map((step, index) => (
                <Step
                  key={index}
                  onClick={() => {
                    handleClickStep(index)
                  }}
                >
                  <StepLabel StepIconComponent={() => stepIcon(index)}>
                    <Typography className='text-sm font-bold text-textHigh'>
                      {step.label}
                    </Typography>
                  </StepLabel>
                  <StepContent>
                    <Typography className='text-xs font-medium text-textMid'>
                      {step.description}
                    </Typography>
                    {index > 0
                      ? (
                        <Box sx={{ mt: 2 }}>
                          <DarkButton
                            type='soft'
                            size='sm'
                            onClick={() => setCurrentStep(index - 1)}
                          >
                            {translate('back')}
                          </DarkButton>
                        </Box>
                        )
                      : (
                        <></>
                        )}
                  </StepContent>
                </Step>
              ))}
            </Stepper>
          </div>
        </div>
      </div>
      <Divider orientation='vertical' flexItem />
      <div className='w-full h-full bg-surfaceWebGrey lg:p-6 md:p-3'>
        <div className='flex justify-end w-full'>
          <div className='hidden md:flex'>
            <DarkIconButton
              type='soft'
              size='sm'
              icon={<Close />}
              onClick={onClose}
            />
          </div>
        </div>
        <div className='flex items-center justify-center w-full h-full'>
          <div className='flex flex-col items-center justify-center h-full md:w-fit w-full md:-mt-3'>
            <div className='sticky top-0 z-50 flex flex-row w-full gap-4 p-4 md:p-6 md:border-b rounded-t-xl bg-surfaceInverse border-secondaryMidStrok'>
              <DarkButton
                fullwidth
                size='sm'
                type={clickedSelection === 'telephone' ? 'contained' : 'soft'}
                onClick={() => chooseService('telephone')}
              >
                {translate('phoneNumbers')}
              </DarkButton>
              <DarkButton
                fullwidth
                size='sm'
                type={clickedSelection === 'MNP75' ? 'contained' : 'soft'}
                onClick={() => chooseService('MNP75')}
              >
                MNP75
              </DarkButton>
            </div>
            {currentStep === 0
              ? (
                  clickedSelection === 'MNP75'
                    ? (
                      <MNP75Step1 onClick={handleNextMNP75} />
                      )
                    : (
                      <UnitStep1 handleNext={handleNextUnit} />
                      )
                )
              : currentStep === 1
                ? (
                    clickedSelection === 'MNP75'
                      ? (
                        <MNP75Step2
                          onClick={handleNextMNP75}
                          invoice={invoiceMNP75}
                          option={option}
                        />
                        )
                      : (
                        <UnitStep2
                          handleNext={handleNextUnit}
                          invoice={invoiceUnit}
                          option={product}
                        />
                        )
                  )
                : (
                  <div />
                  )}
          </div>
        </div>
      </div>
    </div>
  )
}
export default UnitServices
